import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './styles/RecentReviews.css';
import StarRating from './StarRating';

const RecentReviews = ({ reviews }) => {
  const [activeFilter, setActiveFilter] = useState('all');
  const navigate = useNavigate();

  // Helper function to determine if a review is useful
  const isUseful = (review) => {
    return review.usefulnessScore && review.usefulnessScore >= 0.6; // Threshold for usefulness
  };

  // Helper function to determine if a review is very valuable
  const isVeryValuable = (review) => {
    return review.usefulnessScore && review.usefulnessScore >= 0.8; // High usefulness threshold
  };

  // Helper function to check if usefulness data is available
  const hasUsefulnessData = (review) => {
    return review.usefulnessScore !== null && 
           review.usefulnessScore !== undefined && 
           review.usefulnessScore !== 0;
  };

  // Helper function to check if sentiment data is available
  const hasSentimentData = (review) => {
    return review.sentimentLabel && 
           review.sentimentLabel !== null && 
           review.sentimentLabel !== 'unknown';
  };

  // Filter reviews based on selected filter
  const filteredReviews = reviews.filter(review => {
    switch (activeFilter) {
      case 'positive':
        return hasSentimentData(review) && review.sentimentLabel === 'positive';
      case 'negative':
        return hasSentimentData(review) && review.sentimentLabel === 'negative';
      case 'useful':
        return hasUsefulnessData(review) && isUseful(review);
      case 'veryValuable':
        return hasUsefulnessData(review) && isVeryValuable(review);
      default:
        return true; // All reviews
    }
  });

  // Calculate counts for each filter
  const getCounts = () => {
    return {
      all: reviews.length,
      positive: reviews.filter(r => hasSentimentData(r) && r.sentimentLabel === 'positive').length,
      negative: reviews.filter(r => hasSentimentData(r) && r.sentimentLabel === 'negative').length,
      useful: reviews.filter(r => hasUsefulnessData(r) && isUseful(r)).length,
      veryValuable: reviews.filter(r => hasUsefulnessData(r) && isVeryValuable(r)).length
    };
  };

  const counts = getCounts();

  // Format date in a readable way
  const formatDate = (date) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(date).toLocaleDateString(undefined, options);
  };

  return (
    <div className="recent-reviews">
      <div className="reviews-header">
        <h2>Recent Reviews</h2>
        <div className="filter-tabs">
          <div 
            className={`filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
            onClick={() => setActiveFilter('all')}
          >
            All ({counts.all})
          </div>
          <div 
            className={`filter-tab ${activeFilter === 'positive' ? 'active' : ''}`}
            onClick={() => setActiveFilter('positive')}
          >
            Positive ({counts.positive})
          </div>
          <div 
            className={`filter-tab ${activeFilter === 'negative' ? 'active' : ''}`}
            onClick={() => setActiveFilter('negative')}
          >
            Negative ({counts.negative})
          </div>
          <div 
            className={`filter-tab ${activeFilter === 'useful' ? 'active' : ''}`}
            onClick={() => setActiveFilter('useful')}
          >
            Useful ({counts.useful})
          </div>
          <div 
            className={`filter-tab ${activeFilter === 'veryValuable' ? 'active' : ''}`}
            onClick={() => setActiveFilter('veryValuable')}
          >
            Very Valuable ({counts.veryValuable})
          </div>
        </div>
      </div>      <div className="reviews-list">
        {filteredReviews.length > 0 ? (
          filteredReviews.map((review, index) => (
            <div key={index} className="review-item">
              <div className="review-header">
                <div className="review-rating">
                  <StarRating rating={review.rating} size="small" />
                  <span className="rating-value">{review.rating.toFixed(1)}</span>
                </div>
                <div className="review-badges">
                  {/* Very Valuable Badge - only show if data is available */}
                  {hasUsefulnessData(review) && isVeryValuable(review) && (
                    <span className="badge valuable-badge">
                      💎 Very Valuable
                    </span>
                  )}
                  {/* Useful Badge - only show if data is available and not very valuable */}
                  {hasUsefulnessData(review) && isUseful(review) && !isVeryValuable(review) && (
                    <span className="badge useful-badge">
                      ⭐ Useful
                    </span>
                  )}
                  {/* Sentiment Badge - only show if sentiment data is available */}
                  {hasSentimentData(review) && (
                    <span className={`badge sentiment-badge sentiment-${review.sentimentLabel}`}>
                      {review.sentimentLabel === 'positive' ? '😊' : '😞'} {review.sentimentLabel}
                    </span>
                  )}
                  {/* Processing indicator when analysis is pending */}
                  {!hasUsefulnessData(review) && !hasSentimentData(review) && (
                    <span className="badge processing-badge">
                      ⏳ Analysis Pending
                    </span>
                  )}
                </div>
                <div className="review-date">
                  {review.date ? formatDate(review.date) : 'Date unavailable'}
                </div>
              </div>              <div className="review-text">
                {review.text}
              </div>
              <div className="review-actions">
                <button 
                  className="view-btn"
                  // Use review.submission_id for navigation
                  onClick={() => navigate(`/dashboard/feedback-session/${review.submission_id}`)}
                  // Disable button if submission_id is missing
                  disabled={!review.submission_id} 
                >
                  View Full Review
                </button>
                {/*<button className="analyze-btn">Analyze with AI</button>*/}
              </div>
            </div>
          ))
        ) : (
          <div className="no-reviews">No reviews match the selected filter.</div>
        )}
      </div>
    </div>
  );
};

export default RecentReviews;